// Sentry へ送るイベント・パンくずから、個人情報と使用中のトークンを落とす。
// web（propally_web/src/utilities）と agent（propally_agent/src/api）に同じ内容で置いているので、直すときは両方そろえること。
//
// - console 経由: CaptureConsole は console.error / warn の引数を extra.arguments に、SDK は console のパンくずにも載せる。
//   axios / superagent のエラーはリクエスト本文・ヘッダ・応答を持つため、オブジェクトはエラー名とメッセージだけにする。
// - URL 経由: パスワード再設定（prt）・メール確認（ect）・代理ログイン（one_time_code）のトークンは画面の URL に載り、
//   SDK はイベントの request.url（location.href）・画面遷移のパンくず・Session Replay に URL を記録する。値だけ伏せる。
type SentryEventLike = { extra?: Record<string, unknown>; request?: { url?: string }; urls?: unknown };
type SentryBreadcrumbLike = { category?: string; message?: string; data?: Record<string, unknown> };
type ReplayRecordingEventLike = { type?: number; data?: { payload?: { description?: unknown } } };

const FILTERED = "[Filtered]";
const FILTERED_OBJECT = "[Filtered object]";
const SENSITIVE_QUERY_PARAM = /([?&](?:prt|ect|one_time_code)=)[^&#]*/g;
const HAS_SENSITIVE_QUERY_PARAM = /[?&](?:prt|ect|one_time_code)=/;
// rrweb のカスタムイベント（画面遷移などの performanceSpan）
const REPLAY_CUSTOM_EVENT = 5;

export const hasSensitiveUrlParam = (url: string): boolean => HAS_SENSITIVE_QUERY_PARAM.test(url);

export const maskSensitiveUrl = <T>(url: T): T =>
  (typeof url === "string" ? url.replace(SENSITIVE_QUERY_PARAM, `$1${FILTERED}`) : url) as T;

const scrubArgument = (arg: unknown): unknown => {
  if (arg === null || typeof arg !== "object") return arg;
  const { name, message } = arg as { name?: unknown; message?: unknown };
  if (typeof message === "string") {
    return { name: typeof name === "string" ? name : undefined, message };
  }
  return FILTERED_OBJECT;
};

export const scrubSentryEvent = <T extends SentryEventLike>(event: T): T => {
  const args = event.extra?.arguments;
  if (Array.isArray(args) && event.extra) {
    event.extra.arguments = args.map(scrubArgument);
  }
  if (event.request?.url) {
    event.request.url = maskSensitiveUrl(event.request.url);
  }
  // Session Replay のイベント（replay_event）が持つ訪問 URL の一覧
  if (Array.isArray(event.urls)) {
    event.urls = event.urls.map(maskSensitiveUrl);
  }
  return event;
};

export const scrubSentryBreadcrumb = <T extends SentryBreadcrumbLike>(breadcrumb: T): T => {
  const { data } = breadcrumb;
  if (breadcrumb.category === "console" && data && Array.isArray(data.arguments)) {
    data.arguments = data.arguments.map(scrubArgument);
  }
  if (data) {
    // navigation は from / to、xhr / fetch は url に URL が入る
    ["from", "to", "url"].forEach((key) => {
      if (key in data) data[key] = maskSensitiveUrl(data[key]);
    });
  }
  if (breadcrumb.message) {
    breadcrumb.message = maskSensitiveUrl(breadcrumb.message);
  }
  return breadcrumb;
};

// Session Replay の録画データ（Replay の beforeAddRecordingEvent に渡す）。SDK がここに渡すのはカスタムイベントだけで、
// 録画冒頭のページ URL（rrweb の Meta）は伏せられない。そのため agent ではトークン付きの画面でのエラーは録画しない（beforeErrorSampling）。
// パンくずは scrubSentryBreadcrumb を通った後のものが入る
export const scrubReplayRecordingEvent = <T>(event: T): T => {
  const recording = event as unknown as ReplayRecordingEventLike;
  if (recording.type === REPLAY_CUSTOM_EVENT && recording.data?.payload) {
    recording.data.payload.description = maskSensitiveUrl(recording.data.payload.description);
  }
  return event;
};
